import React__default, { useState, useEffect, forwardRef, createElement } from 'react';
import { registerPlugin, FilePond } from 'react-filepond';
import { Box, Text } from '@chakra-ui/layout';
import { FormControl, FormErrorMessage } from '@chakra-ui/form-control';
import { motion } from 'framer-motion';
import FilePondPluginImagePreview from 'filepond-plugin-image-preview';
import FilePondPluginMediaPreview from 'filepond-plugin-media-preview';
import FilePondPluginFileValidateSize from 'filepond-plugin-file-validate-size';
import FilePondPluginFileValidateType from 'filepond-plugin-file-validate-type';
import Button from '@laliga-cdac/button';
import 'filepond/dist/filepond.min.css';
import 'filepond-plugin-image-preview/dist/filepond-plugin-image-preview.css';
import 'filepond-plugin-media-preview/dist/filepond-plugin-media-preview.min.css';
import { Spinner } from '@chakra-ui/spinner';

function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

var TemplateLabel = function TemplateLabel(_ref) {
  var label = _ref.label,
      buttonLabel = _ref.buttonLabel,
      filepond = _ref.filepond,
      browseCallback = _ref.browseCallback;

  var openDialog = function openDialog(e) {
    var _filepond$current;

    console.log(filepond);
    filepond == null ? void 0 : (_filepond$current = filepond.current) == null ? void 0 : _filepond$current.browse();
    if (browseCallback) browseCallback(e);
  };

  return React__default.createElement(Box, {
    display: "flex",
    flexDirection: "column",
    marginTop: "3"
  }, React__default.createElement(Text, null, label), buttonLabel && React__default.createElement(Button, {
    onClick: openDialog,
    colorScheme: "corporateBlack",
    pointerEvents: "all"
  }, buttonLabel));
};

var variants = {
  visible: {
    opacity: 1,
    y: 0
  },
  unseen: {
    opacity: 0,
    y: 50
  }
};
/**
 * Cutom hook para evitar drag n drop en el componente
 * con la ventana del buscador de imagenes abierta.
 * @returns Object
 */

var useConditionalDrop = function useConditionalDrop() {
  var _React$useState = useState(true),
      opened = _React$useState[0],
      setOpened = _React$useState[1];

  var browseCallback = function browseCallback() {
    return setOpened(false);
  };

  var checkClosed = function checkClosed() {
    if (!opened) {
      setOpened(true);
    }
  };

  useEffect(function () {
    window.addEventListener('focus', checkClosed);
    return function () {
      window.removeEventListener('focus', checkClosed);
    };
  }, [opened]);
  return {
    opened: opened,
    browseCallback: browseCallback
  };
};

var _excluded = ["boxProps", "buttonLabel", "error", "files", "filepondCSS", "fileRef", "isInvalid", "label", "isLoading"];
registerPlugin(FilePondPluginFileValidateType, FilePondPluginImagePreview, FilePondPluginMediaPreview, FilePondPluginFileValidateSize);
var MotionBox = /*#__PURE__*/motion(Box);
var FileUpload = /*#__PURE__*/forwardRef(function (props, ref) {
  var boxProps = props.boxProps,
      buttonLabel = props.buttonLabel,
      error = props.error,
      files = props.files,
      filepondCSS = props.filepondCSS,
      isInvalid = props.isInvalid,
      label = props.label,
      isLoading = props.isLoading,
      rest = _objectWithoutPropertiesLoose(props, _excluded);

  var _useConditionalDrop = useConditionalDrop(),
      browseCallback = _useConditionalDrop.browseCallback,
      opened = _useConditionalDrop.opened;

  var _React$useState = useState(),
      showLabel = _React$useState[0],
      setShowLabel = _React$useState[1];

  var animate = showLabel ? 'visible' : 'unseen';
  useEffect(function () {
    var _ref$current, _ref$current$getFiles;

    (ref == null ? void 0 : (_ref$current = ref.current) == null ? void 0 : (_ref$current$getFiles = _ref$current.getFiles()) == null ? void 0 : _ref$current$getFiles.length) > 0 ? setShowLabel(false) : setShowLabel(true);
  }, [files]);
  /**
   * Efecto para eliminar el evento que abre el dialogo de buscar imagen desde el espacio de drop
   */

  useEffect(function () {
    var element = document.getElementsByClassName('filepond--drop-label')[0];

    if (element) {
      var _element$parentNode;

      var elClone = element.cloneNode(true);
      (_element$parentNode = element.parentNode) == null ? void 0 : _element$parentNode.replaceChild(elClone, element);
    }
  }, [ref]);
  return createElement(FormControl, {
    isInvalid: isInvalid || !!error,
    w: "full"
  }, createElement(Box, Object.assign({
    position: "relative",
    minH: "100",
    borderRadius: "xl",
    overflow: "hidden",
    sx: getBoxStyles(boxProps, filepondCSS),
    border: !!error ? '1px' : 0,
    borderColor: !!error ? 'corporateRed.500' : 'inherit',
    backgroundColor: !!error ? 'corporateRed.50' : 'corporateGrey.50'
  }, boxProps), createElement(MotionBox, {
    alignItems: rest.allowMultiple ? 'flex-start' : 'center',
    display: "flex",
    height: "100%",
    justifyContent: "center",
    pointerEvents: "none",
    position: "absolute",
    width: "100%",
    zIndex: "4",
    initial: "visible",
    variants: variants,
    animate: rest.allowMultiple ? 'visible' : animate
  }, isLoading ? createElement(Spinner, {
    thickness: "4px",
    speed: "0.65s",
    emptyColor: "gray.200",
    color: "gray.400",
    size: "xl"
  }) : createElement(TemplateLabel, {
    label: label || 'Arrastra y suelta tus archivos o ',
    buttonLabel: buttonLabel || 'Búscalos',
    filepond: ref,
    browseCallback: browseCallback
  })), createElement(FilePond, Object.assign({
    id: "fileInput",
    files: files,
    labelIdle: "",
    allowDrop: ((rest == null ? void 0 : rest.allowDrop) || opened) && !isLoading,
    // @ts-ignore
    ref: ref,
    credits: false
  }, rest))), createElement(FormErrorMessage, null, error));
});

function getBoxStyles(boxProps, filepondCSS) {
  return _extends({
    '.filepond--panel-root': {
      backgroundColor: 'transparent'
    },
    '.filepond--root': {
      marginBottom: 0,
      maxHeight: '20em'
    },
    '.filepond--root .filepond--drop-label': {
      height: (boxProps == null ? void 0 : boxProps.height) || '5',
      minHeight: (boxProps == null ? void 0 : boxProps.minHeight) || '5'
    }
  }, filepondCSS);
}

export default FileUpload;